import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Combines multiple class names using clsx and tailwind-merge
 * Preserves custom typography classes by handling them separately
 * @param inputs - Array of class values to be merged
 * @returns Merged and optimized class string
 */
export function cn(...inputs: ClassValue[]) {
  // First, get all the classes using clsx
  const classes = clsx(inputs);

  // Split classes into typography and non-typography classes
  const typographyClasses = classes.split(' ').filter((cls) => cls.startsWith('text-'));
  const nonTypographyClasses = classes.split(' ').filter((cls) => !cls.startsWith('text-'));

  // Merge non-typography classes with tailwind-merge
  const mergedClasses = twMerge(nonTypographyClasses.join(' '));

  // Combine typography classes with merged classes
  return [...new Set([...typographyClasses, mergedClasses])].join(' ');
}

/**
 * Checks if a given date falls on a weekend
 * @param date - The date to check
 * @returns True if the date is a Saturday or Sunday, false otherwise
 */
export function isWeekend(date: Date) {
  return date.getDay() === 0 || date.getDay() === 6;
}

/**
 * Formats a number as a currency string with proper decimal places and grouping
 * @param amount - The number to format
 * @param currencyIsoCode - The ISO 4217 currency code
 * @param currencyDecimals - Number of decimal places to use in formatting
 * @param includeCurrencySymbol - Whether to include the currency symbol (default: false)
 * @returns Formatted string with optional currency symbol (e.g., "$1,234.56" or "1,234.56")
 */
export function formatCurrency(
  amount: number,
  currencyIsoCode: string,
  includeCurrencySymbol: boolean = false,
  currencyDecimalsOverride?: number,
): string {
  // Intl.NumberFormat validates `currency` as ISO 4217 even when style is
  // 'decimal' (where the currency value would be ignored for output) and
  // throws RangeError on non-ISO inputs. Only forward `currency` when we
  // actually need it for the currency style.
  return amount.toLocaleString(undefined, {
    style: includeCurrencySymbol ? 'currency' : 'decimal',
    minimumFractionDigits: currencyDecimalsOverride,
    maximumFractionDigits: currencyDecimalsOverride,
    ...(includeCurrencySymbol ? { currency: currencyIsoCode } : {}),
  });
}

/**
 * Determines the appropriate icon to use based on file type/extension
 * @param fileType - The file extension or type identifier (e.g., 'pdf', 'doc', 'png')
 * @returns Icon name to be used for rendering the file type visual representation
 */
export function getFileIconName(fileType: string) {
  let iconName: 'file-pdf' | 'file-xls' | 'file-doc' | 'file-zip' | 'file-img' | 'file-unknown' =
    'file-unknown';

  const pdfIconFileTypes = ['pdf'];
  const xlsIconFileTypes = ['xls', 'xlsx', 'csv', 'excel_x', 'excel'];
  const docIconFileTypes = ['doc', 'docx', 'word_x', 'word'];
  const zipIconFileTypes = ['zip', '7z', 'arc', 'bz', 'bz2'];
  const imgIconFileTypes = [
    'avif',
    'bmp',
    'gif',
    'ico',
    'jpeg',
    'jpg',
    'png',
    'svg',
    'tif',
    'tiff',
    'webp',
  ];

  if (pdfIconFileTypes.includes(fileType)) {
    iconName = 'file-pdf';
  } else if (xlsIconFileTypes.includes(fileType)) {
    iconName = 'file-xls';
  } else if (docIconFileTypes.includes(fileType)) {
    iconName = 'file-doc';
  } else if (zipIconFileTypes.includes(fileType)) {
    iconName = 'file-zip';
  } else if (imgIconFileTypes.includes(fileType)) {
    iconName = 'file-img';
  }

  return iconName;
}

/**
 * Converts URLs in text to clickable HTML links
 * Handles both markdown-style links [text](url) and plain URLs
 * @param text - The text that may contain URLs
 * @returns HTML string with URLs converted to clickable links
 */
export function linkifyText(text: string): string {
  if (!text) {
    return text;
  }

  try {
    const withMarkdownLinks = replaceMarkdownLinks(text);
    const fullyFormattedText = replacePlainLinks(withMarkdownLinks);
    return fullyFormattedText;
  } catch (error) {
    console.error('Failed to linkify text', error);
    return text;
  }
}

/**
 * Replaces markdown-style links [text](url) with HTML anchor tags
 * @param text - The text that may contain markdown links
 * @returns Text with markdown links converted to HTML anchor tags
 */
function replaceMarkdownLinks(text: string): string {
  // Regular expression for markdown-style links
  const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+|www\.[^\s)]+)\)/g;

  return text.replace(markdownLinkRegex, (match, linkText, url) => {
    // Ensure we're not replacing inside existing <a> tags
    if (isInsideAnchorTag(text, match)) {
      return match;
    }
    const formattedUrl = url.startsWith('http') ? url : `http://${url}`;
    return `<a href="${formattedUrl}" target="_blank" rel="noopener noreferrer" class="text-primary hover:text-primary-dark underline">${linkText}</a>`;
  });
}

/**
 * Replaces plain URLs with HTML anchor tags
 * @param text - The text that may contain plain URLs
 * @returns Text with plain URLs converted to HTML anchor tags
 */
function replacePlainLinks(text: string): string {
  // Regular expression for URLs (matches "www" or "http/https")
  const urlRegex = /((https?:\/\/|www\.)[^\s]+)(?!<\/a>)/g;

  return text.replace(urlRegex, (url) => {
    // Ensure we're not replacing inside existing <a> tags
    if (isInsideAnchorTag(text, url)) {
      return url;
    }
    const formattedUrl = url.startsWith('http') ? url : `http://${url}`;
    return `<a href="${formattedUrl}" target="_blank" rel="noopener noreferrer" class="text-primary hover:text-primary-dark underline">${url}</a>`;
  });
}

/**
 * Helper function to check if a match is inside an existing <a> tag
 * @param fullText - The complete text being processed
 * @param match - The text match to check
 * @returns True if the match is inside an existing anchor tag
 */
function isInsideAnchorTag(fullText: string, match: string): boolean {
  const matchIndex = fullText.indexOf(match);
  if (matchIndex === -1) {
    return false;
  }

  // Find the last opening <a> tag before the match
  const beforeMatch = fullText.substring(0, matchIndex);
  const lastOpenTagIndex = beforeMatch.lastIndexOf('<a');
  const lastCloseTagIndex = beforeMatch.lastIndexOf('</a>');

  // If there's an opening tag after the last closing tag, we're inside an anchor
  return lastOpenTagIndex > lastCloseTagIndex;
}
